import { useEffect, useState, type FormEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Hausansicht } from '../components/Hausansicht';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { TabBar } from '../components/TabBar';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, Kennzahl } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { IconObjekte, IconPfeilLinks } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatEuro, formatQm, formatZahl, parseZahlEingabe } from '../lib/format';
import { transition, uebergangSanft } from '../lib/motion';
import {
  type Einheit,
  type EinheitImHaus,
  type HausAntwort,
  type Struktur,
  demoHaus,
  demoHausSpeichern,
  geschossKurz,
  geschossLang,
  kennzahlenAus,
  leereBelegung,
} from '../lib/haus';
import { EinheitAkte } from './EinheitAkte';
import { EtagenAssistent } from './EtagenAssistent';
import { AUSST_LABEL, type ApiObjektVoll, OBJEKTART_LABEL, ObjektFormular, objektartVon } from './ObjektFormular';
import { ObjektEntfernen } from './ObjektLoeschen';
import { Zaehler } from './Zaehler';

/**
 * Objektakte: das Haus im Mittelpunkt. Reiter „Haus“ (Etage für Etage, jede Einheit
 * anklickbar), „Zähler“ und „Stammdaten“. Ein Klick auf eine Einheit öffnet ihre Akte.
 */

export type ObjektBereich = 'haus' | 'zaehler' | 'stammdaten';

export interface ObjektAkteProps {
  objektId: string;
  /** Objekt aus der Liste — für den Kopf während des Ladens und als Vorschau-Grundlage. */
  vorlage?: ApiObjektVoll | null;
  startBereich?: ObjektBereich;
  /** Direkt nach dem Anlegen: Etagen-Assistent öffnen. */
  assistentStarten?: boolean;
  /** Wohn- und Geschäftshaus: Einheiten im Erdgeschoss als Gewerbe vorbelegen. */
  assistentGewerbeImEg?: boolean;
  /** Hinweis beim Öffnen, z. B. nach dem Anlegen. */
  startMeldung?: string;
  onZurueck: () => void;
  onObjektGeaendert?: (o: ApiObjektVoll) => void;
  /** Objekt gelöscht oder archiviert: zurück zur Liste mit Meldung. */
  onEntfernt?: (meldung: string) => void;
  onOeffneAbrechnungen?: () => void;
}

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

function leeresHaus(o: ApiObjektVoll): HausAntwort {
  return {
    objekt: {
      id: o.id,
      bezeichnung: o.bezeichnung,
      anschrift: o.anschrift,
      gesamtflaecheQm: o.gesamtflaecheQm,
      baujahr: o.baujahr,
      mietspiegelQmCent: o.mietspiegelQmCent,
      kappungsgrenzeProzent: o.kappungsgrenzeProzent,
      untergeschosse: o.untergeschosse ?? 0,
      obergeschosse: o.obergeschosse ?? null,
      dachgeschoss: o.dachgeschoss ?? false,
    },
    einheiten: [],
    kennzahlen: kennzahlenAus([]),
  };
}

export function ObjektAkte({
  objektId,
  vorlage,
  startBereich = 'haus',
  assistentStarten = false,
  assistentGewerbeImEg = false,
  startMeldung,
  onZurueck,
  onObjektGeaendert,
  onEntfernt,
  onOeffneAbrechnungen,
}: ObjektAkteProps) {
  const reduziert = useReducedMotion();
  const [haus, setHaus] = useState<HausAntwort | null>(() =>
    DEMO ? (demoHaus(objektId) ?? (vorlage ? leeresHaus(vorlage) : null)) : null,
  );
  const [fehler, setFehler] = useState<string | null>(null);
  const [bereich, setBereich] = useState<ObjektBereich>(startBereich);
  const [einheitId, setEinheitId] = useState<string | null>(null);
  const [assistent, setAssistent] = useState(assistentStarten);
  const [neuAuf, setNeuAuf] = useState<number | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(startMeldung ?? null);

  async function laden() {
    if (DEMO) {
      setHaus(demoHaus(objektId) ?? (vorlage ? leeresHaus(vorlage) : null));
      return;
    }
    try {
      setHaus(await api.get<HausAntwort>(`/objekte/${objektId}/haus`));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Objekt konnte nicht geladen werden.');
    }
  }

  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objektId]);

  useEffect(() => setBereich(startBereich), [startBereich]);

  // Nach jedem Wechsel nach oben scrollen, damit Akte und Haus am Anfang stehen.
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: reduziert ? 'auto' : 'smooth' });
  }, [einheitId, reduziert]);

  function demoAktualisieren(einheiten: EinheitImHaus[], struktur?: Struktur) {
    if (!haus) return;
    const neu: HausAntwort = {
      objekt: { ...haus.objekt, ...(struktur ?? {}) },
      einheiten,
      kennzahlen: kennzahlenAus(einheiten),
    };
    demoHausSpeichern(neu);
    setHaus(neu);
  }

  if (einheitId) {
    return (
      <EinheitAkte
        einheitId={einheitId}
        onZurueck={() => setEinheitId(null)}
        onGeaendert={() => void laden()}
        onEntfernt={(meldung) => {
          setEinheitId(null);
          setErfolg(meldung);
          void laden();
        }}
        onOeffneAbrechnungen={onOeffneAbrechnungen}
      />
    );
  }

  const kopf = haus?.objekt ?? (vorlage ? leeresHaus(vorlage).objekt : null);
  const k = haus?.kennzahlen;
  const etagenOffen = haus !== null && haus.objekt.obergeschosse === null;
  // Eigentumswohnung: nur die eigene Einheit; die übrigen Etagen gehören anderen Eigentümern.
  const etw = haus?.objekt.objektart === 'etw' && (haus?.einheiten.length ?? 0) > 0;
  const struktur: Struktur | null = haus
    ? haus.objekt.obergeschosse === null
      ? { untergeschosse: 0, obergeschosse: 0, dachgeschoss: false }
      : haus.objekt
    : null;

  return (
    <div>
      <button
        type="button"
        onClick={onZurueck}
        className="mb-3 inline-flex items-center gap-1 rounded-md text-sm font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <IconPfeilLinks groesse={16} /> Alle Objekte
      </button>

      <div className="mb-4">
        <h1 className="text-xl font-semibold text-fg">{kopf?.bezeichnung ?? 'Objekt'}</h1>
        {kopf && <p className="mt-0.5 text-sm text-fg-muted">{kopf.anschrift}</p>}
      </div>

      {k && k.einheiten > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiKarte bezeichnung="Einheiten" wert={formatZahl(k.einheiten)} zusatz={formatQm(k.wohnflaecheQm)} />
          <KpiKarte
            bezeichnung="Vermietet"
            wert={`${k.vermietet} von ${k.einheiten - k.eigennutzung}`}
            zusatz={k.eigennutzung > 0 ? `${k.eigennutzung} Eigennutzung` : undefined}
            ton="erfolg"
          />
          <KpiKarte
            bezeichnung="Leerstand"
            wert={formatZahl(k.leer)}
            zusatz={k.leer > 0 ? formatQm(k.leerstandQm) : 'keine'}
            ton={k.leer > 0 ? 'warnung' : 'neutral'}
          />
          <KpiKarte bezeichnung="Soll-Kaltmiete" wert={formatEuro(k.sollKaltmieteCent)} zusatz="je Monat" />
        </div>
      )}

      <div className="mb-4 overflow-x-auto">
        <TabBar
          tabs={[
            { id: 'haus', label: 'Haus' },
            { id: 'zaehler', label: 'Zähler' },
            { id: 'stammdaten', label: 'Stammdaten' },
          ]}
          aktiv={bereich}
          onWechsel={(id) => {
            setBereich(id as ObjektBereich);
            setAssistent(false);
          }}
          ariaLabel="Bereiche des Objekts"
          panelId="objekt-inhalt"
        />
      </div>

      {erfolg && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          role="status"
          className="mb-3 rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-fg"
        >
          {erfolg}
        </motion.p>
      )}

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${bereich}-${assistent ? 'a' : 'h'}`}
          id="objekt-inhalt"
          role="tabpanel"
          initial={reduziert ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduziert ? undefined : { opacity: 0, y: -4 }}
          transition={transition(!!reduziert, uebergangSanft)}
        >
          {fehler && !haus ? (
            <Card>
              <p className="text-sm text-danger">{fehler}</p>
            </Card>
          ) : !haus ? (
            <p className="text-sm text-fg-muted">Wird geladen …</p>
          ) : bereich === 'haus' ? (
            assistent ? (
              <EtagenAssistent
                objekt={haus.objekt}
                bestehende={haus.einheiten}
                gewerbeImEg={assistentGewerbeImEg}
                onAbbruch={() => setAssistent(false)}
                onFertig={({ struktur: s, einheiten }) => {
                  setAssistent(false);
                  setErfolg(
                    haus.einheiten.length === 0
                      ? `${einheiten.length} Einheiten angelegt. Tippen Sie auf eine Einheit, um Mieter und Zähler zu erfassen.`
                      : 'Etagen gespeichert.',
                  );
                  if (DEMO) {
                    const alt = new Map(haus.einheiten.map((e) => [e.id, e]));
                    demoAktualisieren(
                      einheiten.map((e: Einheit) => ({
                        ...e,
                        belegung: alt.get(e.id)?.belegung ?? leereBelegung(),
                        zaehlerAnzahl: alt.get(e.id)?.zaehlerAnzahl ?? 0,
                      })),
                      s,
                    );
                  } else void laden();
                }}
              />
            ) : haus.einheiten.length === 0 && etagenOffen ? (
              <HausAufbauen onStart={() => setAssistent(true)} />
            ) : (
              <div className="space-y-3">
                {etagenOffen && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-accent-soft px-3 py-2.5">
                    <p className="text-sm text-accent-soft-fg">
                      Die Etagen dieses Hauses sind noch nicht festgelegt. Ordnen Sie die Einheiten ihren Etagen zu.
                    </p>
                    <Button groesse="sm" onClick={() => setAssistent(true)}>
                      Etagen festlegen
                    </Button>
                  </div>
                )}
                {neuAuf !== null && (
                  <NeueEinheit
                    objektId={objektId}
                    geschoss={neuAuf}
                    vorhandene={haus.einheiten.filter((e) => e.geschoss === neuAuf).length}
                    onAbbruch={() => setNeuAuf(null)}
                    onAngelegt={(e) => {
                      setNeuAuf(null);
                      setErfolg(`${e.bezeichnung} angelegt.`);
                      if (DEMO) {
                        demoAktualisieren([...haus.einheiten, { ...e, belegung: leereBelegung(), zaehlerAnzahl: 0 }]);
                      } else void laden();
                    }}
                  />
                )}
                <Hausansicht
                  struktur={struktur as Struktur}
                  einheiten={haus.einheiten}
                  onOeffne={(id) => {
                    setErfolg(null);
                    setEinheitId(id);
                  }}
                  onEinheitAnlegen={etagenOffen || etw ? undefined : (g) => setNeuAuf(g)}
                  onEtagenZuordnen={() => setAssistent(true)}
                  nurBelegteEtagen={etw}
                />
                {!etagenOffen && !etw && (
                  <div className="flex justify-end">
                    <Button variante="dezent" groesse="sm" onClick={() => setAssistent(true)}>
                      Aufbau und Etagen ändern
                    </Button>
                  </div>
                )}
              </div>
            )
          ) : bereich === 'zaehler' ? (
            <Zaehler
              initialObjektId={objektId}
              eingebettet
              einheiten={haus.einheiten.map((e) => ({ id: e.id, bezeichnung: e.bezeichnung }))}
            />
          ) : (
            <Stammdaten
              objektId={objektId}
              vorlage={vorlage ?? null}
              onEntfernt={(m) => (onEntfernt ? onEntfernt(m) : onZurueck())}
              onGeaendert={(o) => {
                onObjektGeaendert?.(o);
                if (DEMO && haus) {
                  demoAktualisieren(haus.einheiten);
                  setHaus((h) => (h ? { ...h, objekt: { ...h.objekt, bezeichnung: o.bezeichnung, anschrift: o.anschrift, gesamtflaecheQm: o.gesamtflaecheQm } } : h));
                } else void laden();
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function KpiKarte({
  bezeichnung,
  wert,
  zusatz,
  ton,
}: {
  bezeichnung: string;
  wert: string;
  zusatz?: string;
  ton?: 'neutral' | 'erfolg' | 'warnung';
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3 py-3 shadow-xs">
      <Kennzahl bezeichnung={bezeichnung} wert={wert} zusatz={zusatz} ton={ton} />
    </div>
  );
}

function HausAufbauen({ onStart }: { onStart: () => void }) {
  return (
    <Card>
      <div className="flex flex-col items-center px-2 py-6 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-soft text-accent-soft-fg">
          <IconObjekte groesse={24} />
        </span>
        <h2 className="mt-3 text-lg font-semibold text-fg">Haus aufbauen</h2>
        <p className="mt-1 max-w-md text-sm leading-relaxed text-fg-muted">
          Geben Sie an, wie viele Etagen das Haus hat und wie viele Wohnungen auf jeder Etage liegen. Daraus entsteht die
          Hausansicht mit allen Einheiten — danach erfassen Sie Mieter und Zähler je Wohnung.
        </p>
        <Button className="mt-4" onClick={onStart}>
          Etagen und Wohnungen festlegen
        </Button>
      </div>
    </Card>
  );
}

const LAGEN = ['links', 'mitte links', 'mitte', 'mitte rechts', 'rechts'];

function NeueEinheit({
  objektId,
  geschoss,
  vorhandene,
  onAngelegt,
  onAbbruch,
}: {
  objektId: string;
  geschoss: number;
  vorhandene: number;
  onAngelegt: (e: Einheit) => void;
  onAbbruch: () => void;
}) {
  const [position, setPosition] = useState(vorhandene === 0 ? '' : 'rechts');
  const [bezeichnung, setBezeichnung] = useState(
    [geschossKurz(geschoss), vorhandene === 0 ? null : 'rechts'].filter(Boolean).join(' '),
  );
  const [flaeche, setFlaeche] = useState('');
  const [nutzung, setNutzung] = useState<'wohnen' | 'gewerbe'>('wohnen');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<Einheit | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const qm = parseZahlEingabe(flaeche);
    if (!bezeichnung.trim() || qm === null || qm <= 0) {
      setFehler('Bitte Bezeichnung und eine Wohnfläche größer null angeben.');
      return;
    }
    const daten = {
      bezeichnung: bezeichnung.trim(),
      wohnflaecheQm: qm,
      nutzungsart: nutzung,
      geschoss,
      position: position || null,
      reihenfolge: position ? LAGEN.indexOf(position) + 1 : 0,
    };
    setSendet(true);
    try {
      const neu: Einheit = DEMO
        ? { id: `neu-${Date.now()}`, objektId, zimmer: null, lage: null, megAnteil: null, eigennutzung: false, ...daten }
        : (await api.post<{ einheit: Einheit }>(`/objekte/${objektId}/einheiten`, daten)).einheit;
      setFertig(neu);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Einheit konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  if (fertig) {
    return (
      <Card>
        <SuccessAnimation titel={`${fertig.bezeichnung} angelegt`} groesse={56} onFertig={() => onAngelegt(fertig)} />
      </Card>
    );
  }

  return (
    <Card titel={`Neue Einheit im ${geschossLang(geschoss)}`}>
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_9rem_10rem_9rem]">
          <Input label="Bezeichnung" value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} autoFocus />
          <Input label="Wohnfläche (m²)" inputMode="decimal" value={flaeche} onChange={(e) => setFlaeche(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Lage</span>
            <select
              className={feldKlasse}
              value={position}
              onChange={(e) => {
                setPosition(e.target.value);
                setBezeichnung([geschossKurz(geschoss), e.target.value || null].filter(Boolean).join(' '));
              }}
            >
              <option value="">—</option>
              {LAGEN.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Nutzung</span>
            <select className={feldKlasse} value={nutzung} onChange={(e) => setNutzung(e.target.value as 'wohnen' | 'gewerbe')}>
              <option value="wohnen">Wohnen</option>
              <option value="gewerbe">Gewerbe</option>
            </select>
          </label>
        </div>
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" ladend={sendet}>
            Einheit anlegen
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Stammdaten({
  objektId,
  vorlage,
  onGeaendert,
  onEntfernt,
}: {
  objektId: string;
  vorlage: ApiObjektVoll | null;
  onGeaendert: (o: ApiObjektVoll) => void;
  onEntfernt: (meldung: string) => void;
}) {
  const [objekt, setObjekt] = useState<ApiObjektVoll | null>(DEMO ? vorlage : null);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) return;
    api
      .get<{ objekt: ApiObjektVoll }>(`/objekte/${objektId}`)
      .then((d) => setObjekt(d.objekt))
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Stammdaten konnten nicht geladen werden.'));
  }, [objektId]);

  if (fehler) {
    return (
      <Card>
        <p className="text-sm text-danger">{fehler}</p>
      </Card>
    );
  }
  if (!objekt) return <p className="text-sm text-fg-muted">Wird geladen …</p>;

  if (bearbeiten) {
    return (
      <ObjektFormular
        bestand={objekt}
        onAbbruch={() => setBearbeiten(false)}
        onGespeichert={(o) => {
          setObjekt(o);
          setBearbeiten(false);
          onGeaendert(o);
        }}
      />
    );
  }

  const art = objektartVon(objekt);
  const zeilen: Array<[string, string]> = [
    ['Objektart', art ? OBJEKTART_LABEL[art] : '–'],
    ['Anschrift', objekt.anschrift],
    [art === 'efh' || art === 'etw' ? 'Wohnfläche' : 'Wohn-/Nutzfläche gesamt', formatQm(objekt.gesamtflaecheQm)],
    ['Baujahr', objekt.baujahr ? String(objekt.baujahr) : '–'],
    ['Bundesland', objekt.bundesland ?? '–'],
    ['CO₂-Kennwert', objekt.co2Kennwert === null ? '–' : `${formatZahl(objekt.co2Kennwert)} kg/m²a`],
    ['Mietspiegel', objekt.mietspiegelQmCent ? `${formatEuro(objekt.mietspiegelQmCent)}/m²` : '–'],
    ['Kappungsgrenze', objekt.kappungsgrenzeProzent ? `${objekt.kappungsgrenzeProzent} %` : '–'],
    [
      'Energieausweis',
      [
        objekt.energieausweisArt === 'bedarf' ? 'Bedarfsausweis' : objekt.energieausweisArt === 'verbrauch' ? 'Verbrauchsausweis' : null,
        objekt.energieklasse ? `Klasse ${objekt.energieklasse}` : null,
        objekt.energieKwh !== null ? `${formatZahl(objekt.energieKwh)} kWh/m²a` : null,
      ]
        .filter(Boolean)
        .join(' · ') || '–',
    ],
    ['Darlehen Restschuld', objekt.darlehenRestCent !== null ? formatEuro(objekt.darlehenRestCent) : '–'],
  ];

  return (
    <div className="space-y-4">
    <Card
      titel="Stammdaten"
      aktion={
        <Button variante="sekundaer" groesse="sm" onClick={() => setBearbeiten(true)}>
          Bearbeiten
        </Button>
      }
    >
      <dl className="divide-y divide-border">
        {zeilen.map(([k, v]) => (
          <div key={k} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2">
            <dt className="text-sm text-fg-muted">{k}</dt>
            <dd className="text-sm text-fg">{v}</dd>
          </div>
        ))}
      </dl>
      {objekt.ausstattung.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {objekt.ausstattung.map((a) => (
            <Badge key={a} ton="neutral">
              {AUSST_LABEL[a] ?? a}
            </Badge>
          ))}
        </div>
      )}
    </Card>
    <ObjektEntfernen objekt={objekt} onEntfernt={onEntfernt} />
    </div>
  );
}

export default ObjektAkte;
